/* eslint-disable */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';

interface SubscriptionPlan {
  id: string;
  name: string;
  price_yearly: number;
  features: string[];
}

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState<'checkout' | 'completion'>('checkout');

  // Password State (Step 2)
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadData();
    if (searchParams.get('payment_success') === 'true') {
        setStep('completion');
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const { data: plans } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .limit(1)
        .single();

      if (plans) {
        setPlan({
          ...plans,
          features: Array.isArray(plans.features) ? plans.features : []
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleProceedToPayment = async () => {
    if (!email.includes('@') || !email.includes('.')) {
        setError('Please enter a valid business email address.');
        return;
    }
    setLoading(true);
    
    // Simulating Payment Gateway Handoff
    setTimeout(() => {
        setStep('completion');
        setLoading(false);
    }, 1500);
  };

  const handleFinalizeAccount = async () => {
    if (passwordData.password !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      // 1. Create Auth User
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email,
        password: passwordData.password,
      });

      if (authError || !authData.user) throw authError || new Error('Signup failed');

      // 2. Create Profile
      await supabase.from('profiles').insert({
          id: authData.user.id,
          email: email,
          role: 'Company User'
      });

      // 3. Provision Account
      await createCompanyAndSubscription(authData.user.id);

    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const createCompanyAndSubscription = async (userId: string) => {
    if (!plan) return;
    const now = new Date();
    const periodEnd = new Date(now);
    periodEnd.setFullYear(periodEnd.getFullYear() + 1); // Strictly Yearly

    // Create Company
    const { data: comp } = await supabase.from('companies')
        .insert({ name: 'My Organization', subscription_status: 'active' })
        .select().single();

    if (comp) {
        await supabase.from('company_users').insert({ user_id: userId, company_id: comp.id });
        
        await supabase.from('company_subscriptions').insert({
            company_id: comp.id,
            plan_id: plan.id,
            status: 'active',
            billing_cycle: 'yearly',
            current_period_start: now.toISOString(),
            current_period_end: periodEnd.toISOString()
        });
        
        await supabase.from('payment_transactions').insert({
            company_id: comp.id,
            amount: plan.price_yearly,
            currency: 'USD',
            status: 'completed',
            payment_method: 'card'
        });

        navigate('/company/first-run');
    }
  };

  if (!plan) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f9fafb' }}>
        <div className="spinner"></div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0fdf4', padding: '2rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      
      {/* HEADER with Back Button */}
      <div style={{ maxWidth: '900px', width: '100%', marginBottom: '2rem', display: 'flex', alignItems: 'center' }}>
        <button 
            onClick={() => navigate('/')}
            style={{ 
                background: 'transparent', 
                border: 'none', 
                color: '#059669', // Green theme
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                padding: '0.5rem 0'
            }}
        >
            <svg style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Homepage
        </button>
      </div>

      <div style={{ maxWidth: '900px', width: '100%', display: 'grid', gridTemplateColumns: step === 'checkout' ? '1.2fr 0.8fr' : '1fr', gap: '2rem' }}>
        
        {/* LEFT COLUMN: Checkout Form */}
        {step === 'checkout' ? (
            <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                <div style={{ marginBottom: '2rem' }}>
                    <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>
                        Secure Checkout
                    </h1>
                    <p style={{ color: '#6b7280' }}>
                        Complete your purchase to activate your CSR Dashboard.
                    </p>
                </div>

                {error && (
                    <div style={{ background: '#fee2e2', color: '#dc2626', padding: '0.75rem', borderRadius: '6px', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                        {error}
                    </div>
                )}

                <div style={{ marginBottom: '2rem' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem' }}>
                        Account Email
                    </label>
                    <div style={{ position: 'relative' }}>
                        <input 
                            type="email" 
                            placeholder="admin@yourcompany.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={{ 
                                width: '100%', 
                                padding: '0.875rem 1rem 0.875rem 2.5rem', 
                                border: '1px solid #d1d5db', 
                                borderRadius: '6px',
                                fontSize: '1rem',
                                transition: 'border-color 0.2s',
                                outlineColor: '#059669' // Green focus
                            }}
                        />
                        {/* Mail Icon SVG */}
                        <svg style={{ position: 'absolute', left: '12px', top: '14px', width: '20px', height: '20px', color: '#9ca3af' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>
                        We'll send your receipt and login instructions here.
                    </p>
                </div>

                <div style={{ marginBottom: '2rem' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem' }}>
                        Payment Details
                    </label>
                    <div style={{ padding: '1rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#6b7280', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <svg style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        You will be redirected to our secure payment partner.
                    </div>
                </div>

                <button 
                    onClick={handleProceedToPayment}
                    disabled={loading}
                    className="btn-primary"
                    style={{ 
                        width: '100%', 
                        padding: '1rem', 
                        fontSize: '1rem', 
                        fontWeight: 600, 
                        borderRadius: '6px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        background: '#059669', // GREEN THEME
                        color: 'white',
                        border: 'none',
                        transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#047857'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#059669'}
                >
                    {loading ? 'Processing...' : `Pay $${plan.price_yearly.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}`}
                    {!loading && <span>→</span>}
                </button>
                
                <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', opacity: 0.6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: '#374151' }}>
                        <svg style={{ width: '12px', height: '12px' }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                        SSL Secured
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#374151' }}>256-bit Encryption</div>
                </div>
            </div>
        ) : (
            // PASSWORD SETUP VIEW
            <div style={{ background: 'white', padding: '3rem', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', maxWidth: '500px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div style={{ width: '60px', height: '60px', background: '#ecfdf5', color: '#059669', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                        <svg style={{ width: '30px', height: '30px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>Payment Successful</h2>
                    <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
                        Set a password to access your dashboard for <br/><strong>{email}</strong>
                    </p>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Password</label>
                    <input type="password" value={passwordData.password} onChange={e => setPasswordData({...passwordData, password: e.target.value})} style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Confirm Password</label>
                    <input type="password" value={passwordData.confirmPassword} onChange={e => setPasswordData({...passwordData, confirmPassword: e.target.value})} style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
                </div>

                <button 
                    onClick={handleFinalizeAccount} 
                    disabled={loading} 
                    style={{ 
                        width: '100%', 
                        padding: '1rem', 
                        background: '#059669', // GREEN THEME
                        color: 'white', 
                        fontWeight: 600, 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer' 
                    }}
                >
                    {loading ? 'Creating Dashboard...' : 'Access Dashboard'}
                </button>
            </div>
        )}

        {/* RIGHT COLUMN: Order Summary (Only visible in Checkout step) */}
        {step === 'checkout' && (
            <div style={{ paddingTop: '1rem' }}>
                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#334155', marginBottom: '1.5rem' }}>Order Summary</h3>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{plan.name} (Yearly)</span>
                        <span style={{ fontWeight: 700, color: '#1e293b' }}>${plan.price_yearly.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</span>
                    </div>

                    <ul style={{ margin: '0 0 1.5rem 0', padding: 0, listStyle: 'none' }}>
                        {plan.features.map((feature, i) => (
                            <li key={i} style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                                <span style={{ color: '#059669', fontWeight: 'bold' }}>✓</span> {feature}
                            </li>
                        ))}
                    </ul>

                    <div style={{ borderTop: '1px solid #e2e8f0', margin: '1rem 0' }}></div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>Total Due</span>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#059669' }}>
                                ${plan.price_yearly.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Billed annually</div>
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                        By completing this transaction, you agree to our <a href="#" style={{ color: '#64748b' }}>Terms of Service</a>.
                    </p>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Subscription;
